import { AgCharts } from 'ag-charts-react'
import { useEffect, useMemo, useRef } from 'react'
import { CarCoordinate } from '../../types/types'
import carImageSrc from '../../assets/image/carsLightningMcQueen.jpg'

type TrailingCarScatterPlotProps = {
  coordinates: CarCoordinate[]
  xAxisLabel?: string
  yAxisLabel?: string
}

const TrailingCarScatterPlot = ({
  coordinates,
  xAxisLabel,
  yAxisLabel
}: TrailingCarScatterPlotProps) => {
  const validCoordinates = useMemo(
    () => coordinates.filter(c => c.x !== undefined && c.y !== undefined),
    [coordinates]
  )

  const carImageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const img = new Image()
    img.src = carImageSrc
    carImageRef.current = img
  }, [])

  const chartOptions = useMemo(() => {
    const trailData = validCoordinates.map(c => ({ x: c.x!, y: c.y! }))

    // Compute axis bounds with padding
    const allX = [0, ...trailData.map(d => d.x)]
    const allY = [0, ...trailData.map(d => d.y)]
    const xMin = Math.min(...allX)
    const xMax = Math.max(...allX)
    const yMin = Math.min(...allY)
    const yMax = Math.max(...allY)
    const xPad = Math.max((xMax - xMin) * 0.15, 0.5)
    const yPad = Math.max((yMax - yMin) * 0.15, 0.5)

    return {
      height: 300,
      padding: { top: 20, right: 20, bottom: 20, left: 20 },
      title: {
        text: 'Trail Car Positions',
        fontSize: 13
      },
      series: [
        {
          type: 'scatter' as const,
          data: [{ x: 0, y: 0 }],
          xKey: 'x',
          yKey: 'y',
          title: 'Lead Car',
          marker: {
            size: 40,
            formatter: (params: any) => {
              return {
                size: 40,
                fill: 'rgba(0,0,0,0)',
                stroke: 'rgba(0,0,0,0)',
                strokeWidth: 0
              }
            }
          }
        },
        {
          type: 'scatter' as const,
          data: trailData,
          xKey: 'x',
          yKey: 'y',
          title: 'Trail Car Positions',
          marker: {
            shape: 'circle' as const,
            size: 10,
            fill: '#2563eb',
            stroke: '#1d4ed8'
          }
        }
      ],
      axes: [
        {
          type: 'number',
          position: 'bottom',
          title: { text: xAxisLabel || 'X (Car Lengths)' },
          min: Math.min(xMin - xPad, -1),
          max: xMax + xPad
        },
        {
          type: 'number',
          position: 'left',
          title: { text: yAxisLabel || 'Y (Car Widths)' },
          min: yMin - yPad,
          max: yMax + yPad
        }
      ],
      legend: {
        position: 'bottom'
      }
    }
  }, [validCoordinates])

  if (validCoordinates.length === 0) return null

  // Calculate lead car image position as percentage within the plot area
  const axes = chartOptions.axes as any[]
  const xAxis = axes[0]
  const yAxis = axes[1]
  const xRange = xAxis.max - xAxis.min
  const yRange = yAxis.max - yAxis.min
  // Origin (0,0) position as fraction of the axis range
  const leadXPct = ((0 - xAxis.min) / xRange) * 100
  const leadYPct = ((yAxis.max - 0) / yRange) * 100

  return (
    <div className="mt-2 border border-solid border-[#e5e7eb] dark:border-neutral-80 rounded-lg overflow-hidden">
      <div className="relative">
        <AgCharts options={chartOptions as any} />
        <img
          src={carImageSrc}
          alt="Lead Car"
          className="absolute pointer-events-none"
          style={{
            height: '70px',
            width: 'auto',
            left: `calc(${leadXPct}% - 20px)`,
            top: `calc(${leadYPct}% - 10px)`,
            transform: 'translate(-30%, -60%)',
            opacity: 0.9
          }}
        />
      </div>
    </div>
  )
}

export default TrailingCarScatterPlot
